import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManRadioButtonGroup, ManRadioButton, ManText } from '@craft-design-system/core-components-react';
import useGraphRadio from '../../utils/useGraphRadio';
import Shimmer_graph from '../shimmer_graph';
import { Card } from 'react-bootstrap';
import PopoverComponent from '../popover';
import { useFilterContext } from '../FilterContext';
import AverageDisplay from './Average_value';
import SegmentedControl from './SegmentedControl';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';
import AIToggleButton from '../AIToggleButton';

const TotalStandingTime = ({ value }) => {
  const [total_standing_time, set_total_standing_time] = useState(null);
  const radio_query = useGraphRadio();
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const [isLoading, setIsLoading] = useState(false);
  const [avg_value, setavg_value] = useState(value.total_standing_time[2]['average']);
  const unit = 'h';
  const [chartType, setChartType] = useState("Bar");
  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [downloadData, setDownloadData] = useState(JSON.parse(value.total_standing_time[3]));

  // Handle segmented chart fetch
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: "Total Standing Time",
        });

        console.log("📊 Segmented chart response:", data);
        
        if (chartType === "Bar") {
          set_total_standing_time(JSON.parse(data.graph_data[0]));
          setDownloadData(JSON.parse(data.graph_data[3]));
        } else {
          set_total_standing_time(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error("❌ Error fetching chart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType]);

  const handleSelectChange1 = (event) => {
    set_Radio_values((prevValues) => ({
      ...prevValues,
      [event.detail.name]: event.detail.value,
    }));
  };

  // Handle radio button fetch
  useEffect(() => {
    if (radio_buttons_test) {
      console.log("🔄 Radio button changed:", radio_buttons_test);

      setIsLoading(true);

      const requestParams = {
        vins_short: [value.vin_short],
        radio_value: radio_buttons_test,
        columnname: "Total Standing Time",
        startDate,
        endDate,
        filterdata: filterdata,
        resetdata: filterdata?.resetdata
      };

      console.log("📡 Fetching graph data with:", requestParams);

      radio_query(requestParams)
        .then(data => {
          console.log("✅ Response from radio_query:", data);

          try {
            const graphData = JSON.parse(data.jsonData.graph_data[0]);
            const avgVal = data.jsonData.graph_data[2]?.average;

            set_total_standing_time(graphData);
            setavg_value(avgVal);
            setDownloadData(JSON.parse(data.jsonData.graph_data[3]));
            
            console.log("📊 Graph updated:", graphData);
            console.log("📈 Average value:", avgVal);
          } catch (err) {
            console.error("❌ Error parsing graph_data:", err);
          }
        })
        .catch(error => {
          console.error("❌ Error in radio_query:", error);
        })
        .finally(() => {
          console.log("⏳ Finished radio_query fetch cycle");
          setIsLoading(false);
        });
    }
  }, [radio_buttons_test]);

  // Initial fallback data
  useEffect(() => {
    if (!total_standing_time && value?.total_standing_time?.[0]) {
      try {
        set_total_standing_time(JSON.parse(value.total_standing_time[0]));
        setDownloadData(JSON.parse(value.total_standing_time[3]));
      } catch (error) {
        console.error("Error parsing fallback total_standing_time JSON", error);
      }
    }
  }, [value, total_standing_time]);


  


  return (
    <Card className="card_ride">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '12px',
        }}
      >
        <div style={{ flex: 1, textAlign: 'center' }}>
          <ManText className="card-title-text" variant="component-l" as="span" size="l">
            Daily total standing time (engine running)
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flexDirection: "row-reverse"
        }}>
          <PopoverComponent
            id="standing_time"
            content="<b>Total Standing Time:</b> This KPI displays the total standing time of the vehicle (with running engine) within one calendar day. During this time the vehicle is standing and consuming energy. The charging process is not part of the KPI.<br/><br/><b>Condition for standing:</b><br/>Sum of time where vehiclespeedclass_value_filled IN ('SC_STANDSTILL') AND highvoltsysmode_value_filled = 'HV_DRIVE'<br/><br/><b>Time Series Data:</b><br/>If multiple vehicles are selected average values are displayed. Days without data are not taken into account for the calculation.<br/><br/><b>Histogram:</b><br/>Each bar represents the number of days that fall within a certain range (bin) of <b>daily standing time values</b>. All daily values since start of data acquisition for the selected vehicle(s) are visualized per default.<br/><br/><b>Boxplot:</b><br/>The boxplot shows the <b>distribution of daily standing time values</b> for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the <b>combined distribution</b> of all their daily values.<br/>• The <b>box</b> represents the <b>middle 50%</b> of values (from the 25th percentile ('q1') to the 75th percentile ('q3'))<br/>• The <b>line inside the box</b> shows the <b>median</b> (50th percentile)<br/>• The whiskers extend to show the range of typical values ('min' and 'max') and visualize <b>the spread of the data</b> beyond the central box."
          />
          <Zoom
            chartData={total_standing_time?.data}
            chartLayout={total_standing_time?.layout}
            chartTitle="Daily total standing time (engine running)"
          />
          <DownloadButtonComponent data={downloadData} fileName='total_standing_time_data.csv' />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '20px',
          marginBottom: '16px',
        }}
      >
        {chartType === 'Bar' ? (
          <div style={{ flexShrink: 0 }}>
            <AverageDisplay averages={avg_value} unit="h" />
          </div>
        ) : (
          <div style={{ width: '150px' }} />
        )}

        {chartType === 'Bar' ? (
          <ManRadioButtonGroup
            name="Total Standing Time"
            invalid="false"
            onManChange={handleSelectChange1}
            style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
          >
            <ManRadioButton
              name="Total Standing Time"
              label="Daily"
              value="Daily"
              checked
            />
            <ManRadioButton
              name="Total Standing Time"
              label="Monthly"
              value="Monthly"
            />
            <ManRadioButton
              name="Total Standing Time"
              label="Yearly"
              value="Yearly"
            />
          </ManRadioButtonGroup>
        ) : (
          <div style={{ flexGrow: 1 }} />
        )}

        <div style={{ flexShrink: 0 }}>
          <SegmentedControl selected={chartType} onChange={(type) => setChartType(type)} />
        </div>
      </div>

      {isLoading ? (
        <Shimmer_graph />
      ) : (
        total_standing_time && (
          <>
            <Plot
              data={total_standing_time.data}
              layout={{
                ...total_standing_time.layout,
                autosize: true,
                responsive: true,
                margin: chartType === 'Bar' 
                  ? { t: 10, r: 10, l: 10, b: 10 }
                  : { t: 10, r: 10, l: 10, b: 160 }
              }}
              useResizeHandler={true}
            />

            {/* 🔥 AI Toggle Button - Only show for Bar charts with data */}
            {chartType === 'Bar' && downloadData?.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                paddingLeft: '10px',
                marginTop: '8px',
                marginBottom: '8px'
              }}>
                <AIToggleButton
                  downloadData={downloadData}
                  radioButtonState={radio_buttons_test || { 'Total Standing Time': 'Daily' }}
                  kpiName="Total Standing Time"
                  chartType={chartType}
                  vinShort={value.vin_short}
                  title="AI | Automotive Intelligence - Total Standing Time Analysis"
                />
              </div>
            )}
          </>
        )
      )}
    </Card>
  );
};

TotalStandingTime.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    total_standing_time: PropTypes.array.isRequired,
  }).isRequired,
};

export default TotalStandingTime;
